import { customerTypeEnum, visitStatusEnum } from '@markku/db'
import { z } from 'zod'

// Event type enum
export const eventTypeEnum = z.enum([
  'visit',
  'training',
  'meeting',
  'break',
  'survey',
  'other',
  'availability',
])

export type EventType = z.infer<typeof eventTypeEnum>

// Visit status type (reuse from db enum)
export type VisitStatus = (typeof visitStatusEnum.enumValues)[number]

/** Default filters for KPI dashboard queries — excludes cancelled visits, only counts visit events */
export const KPI_VISIT_FILTERS = {
  eventTypes: ['visit'] as EventType[],
  statuses: ['scheduled', 'in_progress', 'completed'] as VisitStatus[],
}

// Input schemas for stats endpoints
export const visitStatsInputSchema = z.object({
  startDate: z.string().datetime(),
  endDate: z.string().datetime(),
  eventTypes: z.array(eventTypeEnum).optional(),
  statuses: z.array(z.enum(visitStatusEnum.enumValues)).optional(),
  employeeIds: z.array(z.number()).optional(),
  maintenanceTaskIds: z.array(z.number()).optional(),
  customerTypes: z.array(z.enum(customerTypeEnum.enumValues)).optional(),
})

// Weekly visit data
export const weeklyVisitDataSchema = z.object({
  weekStart: z.string(), // ISO date string (Monday of the week)
  weekEnd: z.string(), // ISO date string (Sunday of the week)
  total: z.number(),
  byStatus: z.object({
    unscheduled: z.number(),
    scheduled: z.number(),
    in_progress: z.number(),
    completed: z.number(),
    cancelled: z.number(),
  }),
  byEmployee: z.array(
    z.object({
      odooUserId: z.number(),
      name: z.string(),
      count: z.number(),
    }),
  ),
})

// Weekly revenue data
export const weeklyRevenueDataSchema = z.object({
  weekStart: z.string(),
  weekEnd: z.string(),
  totalRevenue: z.number(),
  byEmployee: z.array(
    z.object({
      odooUserId: z.number(),
      name: z.string(),
      revenue: z.number(),
    }),
  ),
  byBillingType: z.object({
    subscription_included: z.number(),
    separately_invoiced: z.number(),
  }),
})

// Summary statistics
export const visitStatsSummarySchema = z.object({
  totalVisits: z.number(),
  completedVisits: z.number(),
  cancelledVisits: z.number(),
  scheduledVisits: z.number(),
  completionRate: z.number(), // 0-1
  cancellationRate: z.number(), // 0-1
  totalRevenue: z.number(),
  averageRevenuePerVisit: z.number(),
  byEventType: z.array(
    z.object({
      eventType: z.string(),
      count: z.number(),
    }),
  ),
  topEmployees: z.array(
    z.object({
      odooUserId: z.number(),
      name: z.string(),
      visitCount: z.number(),
      completedCount: z.number(),
      scheduledCount: z.number(),
      revenue: z.number(),
    }),
  ),
})

// Response schemas
export const visitStatsWeeklyResponseSchema = z.object({
  weeks: z.array(weeklyVisitDataSchema),
})

export const visitStatsRevenueResponseSchema = z.object({
  weeks: z.array(weeklyRevenueDataSchema),
})

// Daily revenue data (minimal — used for working-day-aligned comparisons)
export const dailyRevenueDataSchema = z.object({
  date: z.string(), // YYYY-MM-DD
  totalRevenue: z.number(),
})

export const visitStatsDailyRevenueResponseSchema = z.object({
  days: z.array(dailyRevenueDataSchema),
})

// ============================================================================
// Capacity-aware revenue forecast (the "uusi" Käyntiennuste)
// ============================================================================

// All date anchors are passed by the client (user-local tz) so the backend
// stays tz-agnostic. monthStart/monthEnd/today/trailingStart are full ISO
// datetimes; the backend derives the YYYY-MM-DD day keys and working-day lists
// from them, matching how the existing daily-revenue forecast is bucketed.
export const revenueForecastInputSchema = z.object({
  monthStart: z.string().datetime(),
  monthEnd: z.string().datetime(),
  today: z.string().datetime(),
  // Start of the trailing window used to derive the per-tech-day run rate.
  trailingStart: z.string().datetime(),
})

export const revenueForecastResponseSchema = z.object({
  // Headline figure: realized-this-month + remaining (booked vs run-rate fill).
  forecast: z.number(),
  // Already delivered this month (completed + in_progress, days before today).
  realizedThisMonth: z.number(),
  // Sum over remaining days of max(booked, capacity-adjusted run-rate).
  remaining: z.number(),
  // Revenue already on the books (scheduled+) for the remaining days.
  bookedRemaining: z.number(),
  // Pure run-rate projection for the remaining working days (before the
  // per-day max with booked) — for transparency in the tooltip.
  runRateRemaining: z.number(),
  // Trailing revenue per available technician-day (the capacity-aware rate).
  revenuePerTechDay: z.number(),
  // Active revenue-generating technicians in the trailing window.
  rosterSize: z.number(),
  remainingWorkingDays: z.number(),
  // Technician-days lost to approved absences across the remaining working
  // days — what the capacity adjustment actually removed from the forecast.
  absenceTechDaysRemaining: z.number(),
  // False when there was no usable roster/capacity and we fell back to a flat
  // trailing daily average (old-style behaviour).
  capacityAware: z.boolean(),
})

export type RevenueForecastInput = z.infer<typeof revenueForecastInputSchema>
export type RevenueForecastResponse = z.infer<
  typeof revenueForecastResponseSchema
>

// Commission schemas
export const commissionRowSchema = z.object({
  userId: z.number(),
  name: z.string(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
  standingCommissionPercent: z.number().nullable(),
  totalRevenue: z.number(),
  autoCommission: z.number(),
  manualCommission: z.number(),
  commission: z.number(),
})

/** @deprecated Use commissionRowSchema */
export const subcontractorCommissionRowSchema = commissionRowSchema

export const commissionsResponseSchema = z.object({
  rows: z.array(commissionRowSchema),
  totals: z.object({
    totalRevenue: z.number(),
    totalAutoCommission: z.number(),
    totalManualCommission: z.number(),
    totalCommission: z.number(),
  }),
})

/** @deprecated Use commissionsResponseSchema */
export const subcontractorCommissionsResponseSchema = commissionsResponseSchema

// Types
export type VisitStatsInput = z.infer<typeof visitStatsInputSchema>
export type WeeklyVisitData = z.infer<typeof weeklyVisitDataSchema>
export type WeeklyRevenueData = z.infer<typeof weeklyRevenueDataSchema>
export type VisitStatsSummary = z.infer<typeof visitStatsSummarySchema>
export type VisitStatsWeeklyResponse = z.infer<
  typeof visitStatsWeeklyResponseSchema
>
export type VisitStatsRevenueResponse = z.infer<
  typeof visitStatsRevenueResponseSchema
>
export type DailyRevenueData = z.infer<typeof dailyRevenueDataSchema>
export type VisitStatsDailyRevenueResponse = z.infer<
  typeof visitStatsDailyRevenueResponseSchema
>
export type CommissionRow = z.infer<typeof commissionRowSchema>
export type CommissionsResponse = z.infer<typeof commissionsResponseSchema>

/** @deprecated Use CommissionRow */
export type SubcontractorCommissionRow = CommissionRow
/** @deprecated Use CommissionsResponse */
export type SubcontractorCommissionsResponse = CommissionsResponse

// ============================================================================
// Cash Flow Forecast (expected invoice payments — income side)
// ============================================================================

// All amounts are GROSS (VAT-inclusive) euros — the cash actually landing on
// the account. The frontend toggles to net (VAT 0 %) by dividing by the VAT
// multiplier, so no separate net fields are stored here.

export const cashFlowAgingBucketKeys = [
  'not_due',
  'overdue_1_30',
  'overdue_31_60',
  'overdue_61_90',
  'overdue_90_plus',
] as const

export const cashFlowTimelineBucketSchema = z.object({
  // ISO date (YYYY-MM-DD) of the due day, or null for the single collapsed
  // "already overdue" bucket.
  date: z.string().nullable(),
  isOverdue: z.boolean(),
  amount: z.number(),
  count: z.number(),
})

export const cashFlowAgingBucketSchema = z.object({
  bucket: z.enum(cashFlowAgingBucketKeys),
  amount: z.number(),
  count: z.number(),
})

export const cashFlowForecastResponseSchema = z.object({
  summary: z.object({
    outstandingAmount: z.number(),
    outstandingCount: z.number(),
    overdueAmount: z.number(),
    overdueCount: z.number(),
    dueNext7Amount: z.number(),
    dueNext30Amount: z.number(),
    medianDaysToPay: z.number().nullable(),
  }),
  timeline: z.array(cashFlowTimelineBucketSchema),
  aging: z.array(cashFlowAgingBucketSchema),
})

export type CashFlowAgingBucketKey = (typeof cashFlowAgingBucketKeys)[number]
export type CashFlowTimelineBucket = z.infer<
  typeof cashFlowTimelineBucketSchema
>
export type CashFlowAgingBucket = z.infer<typeof cashFlowAgingBucketSchema>
export type CashFlowForecastResponse = z.infer<
  typeof cashFlowForecastResponseSchema
>

// ============================================================================
// Acquisition / Marketing Attribution Report
// ============================================================================

export const acquisitionReportInputSchema = z.object({
  startDate: z.string().datetime(),
  endDate: z.string().datetime(),
  model: z.enum(['first_touch', 'last_touch']).default('last_touch'),
  groupBy: z.enum(['source', 'medium', 'campaign']).default('source'),
})

export const acquisitionReportRowSchema = z.object({
  key: z.string(),
  contacts: z.number(),
  opportunities: z.number(),
  wonOpportunities: z.number(),
  visitsBooked: z.number(),
  visitsCompleted: z.number(),
  revenue: z.number(),
})

export const acquisitionReportResponseSchema = z.object({
  rows: z.array(acquisitionReportRowSchema),
})

export type AcquisitionReportInput = z.infer<
  typeof acquisitionReportInputSchema
>
export type AcquisitionReportRow = z.infer<typeof acquisitionReportRowSchema>
export type AcquisitionReportResponse = z.infer<
  typeof acquisitionReportResponseSchema
>

// ============================================================================
// Subscription Statistics
// ============================================================================

// Input schema for subscription stats
export const subscriptionStatsInputSchema = z.object({
  startDate: z.string().datetime(),
  endDate: z.string().datetime(),
  customerTypes: z.array(z.enum(customerTypeEnum.enumValues)).optional(),
})

// Monthly subscription data
export const monthlySubscriptionDataSchema = z.object({
  monthStart: z.string(), // ISO date string (first day of month)
  monthEnd: z.string(), // ISO date string (last day of month)
  mrr: z.number(), // Monthly Recurring Revenue at end of month
  activeCount: z.number(), // Active subscriptions at end of month
  newCount: z.number(), // New subscriptions started in month
  churnedCount: z.number(), // Subscriptions ended/cancelled in month
})

// Summary statistics for subscriptions
export const subscriptionStatsSummarySchema = z.object({
  currentMrr: z.number(),
  activeSubscriptions: z.number(),
  averageSubscriptionValue: z.number(),
  newSubscriptionsInPeriod: z.number(),
  churnedSubscriptionsInPeriod: z.number(),
})

// Response schemas
export const subscriptionMonthlyResponseSchema = z.object({
  months: z.array(monthlySubscriptionDataSchema),
})

// Types
export type SubscriptionStatsInput = z.infer<
  typeof subscriptionStatsInputSchema
>
export type MonthlySubscriptionData = z.infer<
  typeof monthlySubscriptionDataSchema
>
export type SubscriptionStatsSummary = z.infer<
  typeof subscriptionStatsSummarySchema
>
export type SubscriptionMonthlyResponse = z.infer<
  typeof subscriptionMonthlyResponseSchema
>

// ============================================================================
// Employee Utilization Statistics
// ============================================================================

// Employee type filter
export const employeeTypeFilterEnum = z.enum([
  'all',
  'employees',
  'subcontractors',
])
export type EmployeeTypeFilter = z.infer<typeof employeeTypeFilterEnum>

// Input schema for employee utilization stats (extends visit stats input)
export const employeeStatsInputSchema = z.object({
  startDate: z.string().datetime(),
  endDate: z.string().datetime(),
  employeeType: employeeTypeFilterEnum.optional(),
  employeeIds: z.array(z.number()).optional(),
})

// Weekly utilization data per employee
export const weeklyEmployeeUtilizationSchema = z.object({
  weekStart: z.string(),
  weekEnd: z.string(),
  workdayCount: z.number(), // Number of workdays in the week (Mon-Fri)
  capacityHours: z.number(), // workdayCount * 8
  byEmployee: z.array(
    z.object({
      userId: z.number(),
      name: z.string(),
      isSubcontractor: z.boolean(),
      totalHours: z.number(), // Total hours scheduled/worked
      billableHours: z.number(), // Hours from visit event type
      nonBillableHours: z.number(), // Hours from training, meeting, etc.
      utilizationRate: z.number(), // totalHours / capacityHours (0-1+, can exceed 1)
      billableRate: z.number(), // billableHours / totalHours (0-1)
    }),
  ),
})

// Summary statistics for employee utilization
export const employeeStatsSummarySchema = z.object({
  totalEmployees: z.number(),
  employeesCount: z.number(), // Regular employees
  subcontractorsCount: z.number(),
  averageUtilizationRate: z.number(), // 0-1
  totalHoursWorked: z.number(),
  totalBillableHours: z.number(),
  averageRevenuePerHour: z.number(),
  // Feedback stats
  averageRating: z.number().nullable(), // Average rating across all employees (null if no feedbacks)
  feedbackRate: z.number(), // 0-1, percentage of visits with feedback
  totalFeedbackCount: z.number(),
})

// Employee utilization row for table
export const employeeUtilizationRowSchema = z.object({
  userId: z.number(),
  employeeId: z.number(),
  name: z.string(),
  role: z.string(),
  isSubcontractor: z.boolean(),
  totalHours: z.number(),
  billableHours: z.number(),
  nonBillableHours: z.number(),
  utilizationRate: z.number(),
  billableRate: z.number(),
  revenue: z.number(),
  revenuePerHour: z.number(),
  // Feedback stats
  averageRating: z.number().nullable(), // null if no feedbacks
  feedbackCount: z.number(),
  feedbackRate: z.number(), // 0-1, percentage of visits with feedback
  positiveRate: z.number(), // 0-1, percentage of ratings that are 4-5 stars
})

// Response schemas
export const employeeUtilizationWeeklyResponseSchema = z.object({
  weeks: z.array(weeklyEmployeeUtilizationSchema),
})

export const employeeUtilizationTableResponseSchema = z.object({
  rows: z.array(employeeUtilizationRowSchema),
  totals: z.object({
    totalHours: z.number(),
    billableHours: z.number(),
    nonBillableHours: z.number(),
    averageUtilizationRate: z.number(),
    averageBillableRate: z.number(),
    totalRevenue: z.number(),
    averageRevenuePerHour: z.number(),
    // Feedback totals
    averageRating: z.number().nullable(),
    totalFeedbackCount: z.number(),
    feedbackRate: z.number(),
  }),
})

// Per-employee per-day occupancy for the capacity heatmap
export const employeeDailyHoursDaySchema = z.object({
  date: z.string(), // YYYY-MM-DD in local time
  totalMinutes: z.number(),
  visitCount: z.number(),
  titles: z.array(z.string()),
  // Day is occupied by an approved absence or a non-customer event (other).
  // Shown neutral (gray, not red) and excluded from the empty-day tally.
  blocked: z.boolean(),
  // Raw event/absence type (e.g. 'other', 'vacation'); the client translates it
  // with the existing event/absence label maps.
  blockType: z.string().nullable(),
})

// Lean per-day occupancy for the "a week ago" reconstruction — only what the
// week-over-week empty-day delta needs (no titles/visit count).
export const employeeWeekAgoDaySchema = z.object({
  date: z.string(),
  totalMinutes: z.number(),
  blocked: z.boolean(),
})

export const employeeDailyHoursRowSchema = z.object({
  userId: z.number(),
  name: z.string(),
  isSubcontractor: z.boolean(),
  teamId: z.number().nullable(),
  teamName: z.string().nullable(),
  teamLeaderName: z.string().nullable(),
  // Net revenue (VAT 0%) of the row's visits in range, split across attendees
  // so the column sums to total revenue without double-counting shared visits.
  revenue: z.number(),
  days: z.array(employeeDailyHoursDaySchema),
  // Reconstructed occupancy for the same window shifted back one week, used for
  // the week-over-week empty-day delta. Naturally empty when the employee didn't
  // yet exist a week ago, so the delta simply reflects bookings gained since.
  weekAgoDays: z.array(employeeWeekAgoDaySchema),
})

export const employeeDailyHoursHeatmapResponseSchema = z.object({
  employees: z.array(employeeDailyHoursRowSchema),
})

// Types
export type EmployeeStatsInput = z.infer<typeof employeeStatsInputSchema>
export type WeeklyEmployeeUtilization = z.infer<
  typeof weeklyEmployeeUtilizationSchema
>
export type EmployeeStatsSummary = z.infer<typeof employeeStatsSummarySchema>
export type EmployeeUtilizationRow = z.infer<
  typeof employeeUtilizationRowSchema
>
export type EmployeeUtilizationWeeklyResponse = z.infer<
  typeof employeeUtilizationWeeklyResponseSchema
>
export type EmployeeUtilizationTableResponse = z.infer<
  typeof employeeUtilizationTableResponseSchema
>
export type EmployeeDailyHoursDay = z.infer<typeof employeeDailyHoursDaySchema>
export type EmployeeDailyHoursRow = z.infer<typeof employeeDailyHoursRowSchema>
export type EmployeeDailyHoursHeatmapResponse = z.infer<
  typeof employeeDailyHoursHeatmapResponseSchema
>

// ============================================================================
// Employee Commission Statistics
// ============================================================================

// My Commissions
export const myCommissionsInputSchema = z.object({
  monthCount: z.number().min(1).max(24).default(6),
})

export const myCommissionsMonthSchema = z.object({
  month: z.string(), // 'YYYY-MM'
  totalRevenue: z.number(),
  commission: z.number(),
  autoCommission: z.number(),
  manualCommission: z.number(),
  confirmedCommission: z.number(),
  estimatedCommission: z.number(),
})

export const myCommissionsResponseSchema = z.object({
  months: z.array(myCommissionsMonthSchema),
  currentMonthCommission: z.number(),
  currentMonthConfirmedCommission: z.number(),
  currentMonthEstimatedCommission: z.number(),
  isSubcontractor: z.boolean(),
})

// Commission Leaderboard
export const commissionLeaderboardInputSchema = z.object({
  startDate: z.string().datetime(),
  endDate: z.string().datetime(),
})

export const leaderboardEntrySchema = z.object({
  rank: z.number(),
  commission: z.number(),
  totalRevenue: z.number(),
  isCurrentUser: z.boolean(),
  name: z.string().nullable(),
})

export const commissionLeaderboardResponseSchema = z.object({
  entries: z.array(leaderboardEntrySchema),
  totalParticipants: z.number(),
})

// Maintenance Recommendation Stats
export const maintenanceRecommendationStatsResponseSchema = z.object({
  totalReported: z.number(),
  open: z.number(),
  won: z.number(),
  lost: z.number(),
  hitRate: z.number(), // 0-1, won / (won + lost)
})

// Types
export type MyCommissionsInput = z.infer<typeof myCommissionsInputSchema>
export type MyCommissionsMonth = z.infer<typeof myCommissionsMonthSchema>
export type MyCommissionsResponse = z.infer<typeof myCommissionsResponseSchema>
export type CommissionLeaderboardInput = z.infer<
  typeof commissionLeaderboardInputSchema
>
export type LeaderboardEntry = z.infer<typeof leaderboardEntrySchema>
export type CommissionLeaderboardResponse = z.infer<
  typeof commissionLeaderboardResponseSchema
>
export type MaintenanceRecommendationStatsResponse = z.infer<
  typeof maintenanceRecommendationStatsResponseSchema
>

// ============================================================================
// Resident App Statistics
// ============================================================================

export const residentAppStatsInputSchema = z.object({
  startDate: z.string().datetime(),
  endDate: z.string().datetime(),
})

export const residentAppStatsResponseSchema = z.object({
  overview: z.object({
    totalUsers: z.number(), // all-time resident user count (context)
    newUsers: z.number(), // registered in period
    loggedInUsers: z.number(), // unique users who logged in in period
    totalLogins: z.number(), // total login events in period
    marketingConsentCount: z.number(), // consents given in period
  }),
  engagementTiers: z.object({
    oneTime: z.number(), // 1 login in period
    light: z.number(), // 2-5 logins in period
    regular: z.number(), // 6+ logins in period
  }),
  loginActivity: z.array(
    z.object({
      month: z.string(),
      logins: z.number(),
      uniqueUsers: z.number(),
    }),
  ),
  platforms: z.object({
    ios: z.number(),
    android: z.number(),
    desktop: z.number(),
  }),
  maintenanceTasks: z.object({
    totalCreated: z.number(),
    totalCompleted: z.number(),
  }),
  featureUsage: z.object({
    feedbackCount: z.number(),
    feedbackAvgRating: z.number().nullable(),
    chatMessageCount: z.number(),
    propertyEditCount: z.number(),
    fileUploadCount: z.number(),
    termsAcceptedCount: z.number(),
  }),
  marketingConsentMonthly: z.array(
    z.object({
      month: z.string(),
      count: z.number(),
    }),
  ),
})

export type ResidentAppStatsInput = z.infer<typeof residentAppStatsInputSchema>
export type ResidentAppStatsResponse = z.infer<
  typeof residentAppStatsResponseSchema
>

// ============================================================================
// Personal Sales Stats (apps/erp /sales-stats view)
// ============================================================================

const salesPeriodSchema = z.object({
  userId: z.number().optional(),
  startDate: z.string().datetime().optional(),
  endDate: z.string().datetime().optional(),
})

export const salesPersonalSummaryInputSchema = salesPeriodSchema
export const salesPersonalPipelineInputSchema = z.object({
  userId: z.number().optional(),
})
export const salesPersonalRecentWinsInputSchema = z.object({
  userId: z.number().optional(),
  days: z.number().min(1).max(90).default(7),
})
export const salesLeaderboardInputSchema = z.object({
  startDate: z.string().datetime().optional(),
  endDate: z.string().datetime().optional(),
  topN: z.number().min(1).max(20).default(5),
})

const commissionBreakdownSchema = z.object({
  auto: z.number(),
  manual: z.number(),
  total: z.number(),
})

export const salesPersonalSummaryResponseSchema = z.object({
  viewedUserId: z.number(),
  viewedUserName: z.string(),
  viewedFirstName: z.string(),
  startDate: z.string(),
  endDate: z.string(),
  // Revenue from visits the user *sold* in the period (anchored on
  // visits.createdAt; cancelled and subscription_included tasks excluded).
  // The split shows what's already delivered vs still to be performed.
  revenueSold: z.number(),
  revenueDelivered: z.number(),
  revenuePending: z.number(),
  // Sold in the elapsed working-day slice only — input to the daily pace and
  // the apples-to-apples counterpart of `prevPeriodRevenueSliced`.
  revenueSoldElapsed: z.number(),
  prevPeriodRevenueSliced: z.number().nullable(),
  dailyAverage: z.number(),
  forecastEndOfPeriod: z.number(),
  workingDaysElapsed: z.number(),
  remainingWorkingDays: z.number(),
  personalBest12mo: z
    .object({ revenue: z.number(), yearMonth: z.string() })
    .nullable(),
  rolling3moAverage: z.number(),
  commissionSold: commissionBreakdownSchema,
  commissionDelivered: commissionBreakdownSchema,
  commissionPending: commissionBreakdownSchema,
  prevPeriodCommission: commissionBreakdownSchema,
  // Payable view: anchored on visit-date (visits.startTime), the basis for the
  // monthly payout. confirmed = completed (paid at month-end); estimated =
  // scheduled + in_progress (only paid if they complete).
  revenuePayable: z.number(),
  revenuePayableConfirmed: z.number(),
  revenuePayableEstimated: z.number(),
  commissionPayable: commissionBreakdownSchema,
  commissionPayableConfirmed: commissionBreakdownSchema,
  commissionPayableEstimated: commissionBreakdownSchema,
})

export const salesPersonalPipelineResponseSchema = z.object({
  viewedUserId: z.number(),
  openCount: z.number(),
  openValue: z.number(),
  byStage: z.array(
    z.object({
      stage: z.string(),
      count: z.number(),
      value: z.number(),
    }),
  ),
})

export const salesPersonalRecentWinsResponseSchema = z.object({
  viewedUserId: z.number(),
  wins: z.array(
    z.object({
      opportunityId: z.number(),
      name: z.string(),
      value: z.number(),
      wonAt: z.string(),
      customerName: z.string().nullable(),
    }),
  ),
})

export const salesLeaderboardRowSchema = z.object({
  userId: z.number(),
  name: z.string(),
  revenue: z.number(),
  visitCount: z.number(),
  rank: z.number(),
})

export const salesLeaderboardResponseSchema = z.object({
  rows: z.array(salesLeaderboardRowSchema),
  selfRank: z.object({ rank: z.number(), revenue: z.number() }).nullable(),
})

export const salesAttributedUsersResponseSchema = z.array(
  z.object({
    id: z.number(),
    firstName: z.string(),
    lastName: z.string(),
    email: z.string().nullable(),
  }),
)

// Admin-only team commissions overview (apps/erp /sales-team-commissions).
export const salesTeamCommissionsInputSchema = z.object({
  startDate: z.string().datetime().optional(),
  endDate: z.string().datetime().optional(),
})

export const salesTeamCommissionRowSchema = z.object({
  userId: z.number(),
  name: z.string(),
  // Total payable commission (all streams), anchored on completion date — what
  // this person is actually paid for the period.
  ownProvision: z.number(),
  // Completed sales revenue (excl. VAT, tasks only) attributed to this rep.
  salesRevenue: z.number(),
  // 1% of that revenue — this rep's contribution to the team lead's bonus.
  teamLeadCommission: z.number(),
})

export const salesTeamCommissionsResponseSchema = z.object({
  startDate: z.string(),
  endDate: z.string(),
  rows: z.array(salesTeamCommissionRowSchema),
  totals: z.object({
    ownProvision: z.number(),
    salesRevenue: z.number(),
    teamLeadCommission: z.number(),
  }),
})

export type SalesPersonalSummaryInput = z.infer<
  typeof salesPersonalSummaryInputSchema
>
export type SalesPersonalSummaryResponse = z.infer<
  typeof salesPersonalSummaryResponseSchema
>
export type SalesPersonalPipelineInput = z.infer<
  typeof salesPersonalPipelineInputSchema
>
export type SalesPersonalPipelineResponse = z.infer<
  typeof salesPersonalPipelineResponseSchema
>
export type SalesPersonalRecentWinsInput = z.infer<
  typeof salesPersonalRecentWinsInputSchema
>
export type SalesPersonalRecentWinsResponse = z.infer<
  typeof salesPersonalRecentWinsResponseSchema
>
export type SalesLeaderboardInput = z.infer<typeof salesLeaderboardInputSchema>
export type SalesLeaderboardRow = z.infer<typeof salesLeaderboardRowSchema>
export type SalesLeaderboardResponse = z.infer<
  typeof salesLeaderboardResponseSchema
>
export type SalesAttributedUsersResponse = z.infer<
  typeof salesAttributedUsersResponseSchema
>
export type SalesTeamCommissionsInput = z.infer<
  typeof salesTeamCommissionsInputSchema
>
export type SalesTeamCommissionRow = z.infer<
  typeof salesTeamCommissionRowSchema
>
export type SalesTeamCommissionsResponse = z.infer<
  typeof salesTeamCommissionsResponseSchema
>

// ============================================================================
// Monthly Revenue by Team & Segment
// ============================================================================

// One breakdown row within a month — a team or a segment and its revenue.
// `key` is the teamId (number) or customer type (string), or null for the
// "Other / No team" team bucket. `label` is the team name (from DB) for teams;
// for segments it is the raw enum value, which the client localizes.
export const monthlyRevenueBreakdownSchema = z.object({
  key: z.union([z.number(), z.string()]).nullable(),
  label: z.string(),
  revenue: z.number(), // EUR, VAT-excl — completed + inProgress + scheduled
  completedRevenue: z.number(), // VAT-excl, from completed visits
  inProgressRevenue: z.number(), // VAT-excl, from in-progress visits (at estimate)
  scheduledRevenue: z.number(), // VAT-excl, from scheduled visits (at estimate)
})

export const monthlyRevenueDataSchema = z.object({
  month: z.string(), // 'YYYY-MM'
  total: z.number(), // VAT-excl; byTeam and bySegment each sum to this
  byTeam: z.array(monthlyRevenueBreakdownSchema),
  bySegment: z.array(monthlyRevenueBreakdownSchema),
})

export const monthlyVisitRevenueByTeamAndSegmentResponseSchema = z.object({
  months: z.array(monthlyRevenueDataSchema), // exactly 12, oldest → newest
})

export type MonthlyRevenueBreakdown = z.infer<
  typeof monthlyRevenueBreakdownSchema
>
export type MonthlyRevenueData = z.infer<typeof monthlyRevenueDataSchema>
export type MonthlyVisitRevenueByTeamAndSegmentResponse = z.infer<
  typeof monthlyVisitRevenueByTeamAndSegmentResponseSchema
>
